/* eslint-disable no-console */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService, SessionService } from '@core/auth';
import { ResponsiveService } from '@core/services';
import { AppAccountService } from '@features/account/bussines';
import { BerlinDatePipe } from '@shared/berlin-time';
import { ProgressSpinnerComponent } from '@shared/ui';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { formatDistanceToNow, fromUnixTime } from 'date-fns';
import { dateFnsLocaleFor } from '@shared/utils';
import { interval } from 'rxjs';
import packageInfo from 'packageJson';

@Component({
  selector: 'page-account-information',
  templateUrl: 'information.component.html',
  styleUrls: ['information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ProgressSpinnerComponent, TranslocoModule, BerlinDatePipe],
})
export class PageAccountInformationComponent {
  _responsive = inject(ResponsiveService);
  readonly #transloco = inject(TranslocoService);
  private _userAccount = inject(AppAccountService);
  private _authService = inject(AuthService);
  _session = inject(SessionService);

  userData$ = this._userAccount.myUserAccount;

  $loading: WritableSignal<boolean> = signal(false);
  private $interval = toSignal(interval(1000));

  $accessTokenClaims = computed(() => {
    this.$interval();
    return this._authService.getClaims();
  });

  $accessTokenExpiresAt = computed(() => {
    const claims = this.$accessTokenClaims();
    const date = claims?.exp ? fromUnixTime(claims.exp) : undefined;
    return date instanceof Date && !isNaN(date.getTime()) ? date : undefined;
  });

  $accessTokenExpiresIn = computed(() => {
    const accessTokenExpiresAt = this.$accessTokenExpiresAt();
    if (!accessTokenExpiresAt) {
      return '';
    }
    return formatDistanceToNow(accessTokenExpiresAt, {
      addSuffix: true,
      locale: dateFnsLocaleFor(this.#transloco.getActiveLang()),
    });
  });

  get $isDesktop() {
    return this._responsive.$isDesktop;
  }

  get appVersion() {
    return packageInfo.version;
  }

  constructor() {
    this._simulateLoadingAnimation();
  }

  async refreshAccessToken() {
    if (await this._authService.refresh()) {
      console.log('Token refreshed');
    } else {
      console.log('Token refresh failed');
    }
  }

  private _simulateLoadingAnimation() {
    this.$loading.set(true);
    setTimeout(() => {
      this.$loading.set(false);
    }, 150);
  }

  logout() {
    this._session.logout();
  }
}
